import { berlinDateString, berlinMidnightTimestamp } from '../../kits/berlinDateKit';
import type { UserStatus } from '../../shared-state/identity/types';
import { IDLE_CHAIN_RETURNED_STORAGE_KEY, IDLE_CHAIN_STAGE_STORAGE_KEY } from './inactivityStorage';

export type PresenceRule = 'idle-chain' | 'manual-ooo' | 'midnight';
export type IdleChainStage = 'away' | 'ooo';

export interface IInactivityTimers {
    pageIdleMs: number;
    awayToOooMs: number;
    oooToPromptMs: number;
    manualOooMs: number;
    midnightInactivityMs: number;
    /** How long a chain-status mismatch is tolerated as "not synced yet" before the stage is treated as stale. */
    stageSyncGraceMs: number;
}

export interface IRulesInput {
    now: number;
    lastActivity: number;
    inArea: boolean;
    status: UserStatus | undefined;
    idleChainStage: IdleChainStage | null;
    /** Epoch ms the idle-chain stage was written, or null if unknown (e.g. an older tab's stage). */
    idleChainStageAt: number | null;
    oooSelectedManually: boolean;
    promptOpen: boolean;
    /** Epoch ms of a midnight that passed while this tab was open and hasn't been evaluated yet. */
    pendingMidnightAt: number | null;
}

export type RuleDecision =
    | { type: 'none' }
    | { type: 'clear-stage' }
    | { type: 'return-to-available' }
    | { type: 'set-away' }
    | { type: 'set-ooo' }
    | { type: 'open-prompt'; rule: PresenceRule };

const NONE: RuleDecision = { type: 'none' };

const STAGE_STATUS: Record<IdleChainStage, UserStatus> = {
    away: 'away',
    ooo: 'out-of-office'
};

// Statuses the idle chain can legitimately hold while it runs. A status outside this set means the
// user (or another tab) deliberately picked a non-chain status, so the stage is stale and gets cleared.
// A mismatch *within* this set just means a tab hasn't synced the latest chain status yet.
const CHAIN_STATUSES: UserStatus[] = ['available', 'away', 'out-of-office'];

/** Decides the next automatic step. Pure: all state comes in through `input`. */
export function evaluateInactivityRules(input: IRulesInput, timers: IInactivityTimers): RuleDecision {
    const { now, lastActivity, inArea, status, idleChainStage, idleChainStageAt, oooSelectedManually, promptOpen, pendingMidnightAt } = input;
    if (promptOpen) return NONE;

    const inactiveMs = now - lastActivity;

    if (pendingMidnightAt !== null && inArea && lastActivity <= pendingMidnightAt - timers.midnightInactivityMs) {
        return { type: 'open-prompt', rule: 'midnight' };
    }

    if (idleChainStage) {
        if (status !== STAGE_STATUS[idleChainStage]) {
            // A non-chain status means the chain was deliberately left; clear the stale stage immediately.
            if (status === undefined || !CHAIN_STATUSES.includes(status)) return { type: 'clear-stage' };
            // A mismatch among chain statuses is usually a tab that hasn't synced yet: wait, but only for a
            // short grace period after the stage was written. Past that (or with no timestamp at all, e.g. a
            // stage left over from before this device restored a different status) it's stale: clear it so
            // the chain can heal instead of stalling forever.
            if (idleChainStageAt !== null && now - idleChainStageAt < timers.stageSyncGraceMs) return NONE;
            return { type: 'clear-stage' };
        }
        if (inactiveMs < timers.pageIdleMs) return { type: 'return-to-available' };
    }

    if (!inArea) return NONE;

    const awayAt = timers.pageIdleMs;
    const oooAt = awayAt + timers.awayToOooMs;
    const promptAt = oooAt + timers.oooToPromptMs;

    if (!idleChainStage && status === 'available' && inactiveMs >= awayAt) return { type: 'set-away' };
    if (idleChainStage === 'away' && inactiveMs >= oooAt) return { type: 'set-ooo' };
    if (idleChainStage === 'ooo' && inactiveMs >= promptAt) return { type: 'open-prompt', rule: 'idle-chain' };

    if (!idleChainStage && status === 'out-of-office' && oooSelectedManually && inactiveMs >= timers.manualOooMs) {
        return { type: 'open-prompt', rule: 'manual-ooo' };
    }

    return NONE;
}

export interface ITimeoutInput {
    status: UserStatus | undefined;
    idleChainStage: IdleChainStage | null;
    oooSelectedManually: boolean;
    returnDateIsToday: boolean;
}

export type TimeoutAction = 'none' | 'lobby-return-green' | 'lobby-keep-status' | 'sign-out' | 'midnight-keep-ooo' | 'midnight-set-ooo-today';

/** What to do when nobody answered the prompt. Re-checks the rule against the latest state. */
export function resolvePromptTimeout(rule: PresenceRule, input: ITimeoutInput): TimeoutAction {
    switch (rule) {
        case 'idle-chain':
            return input.idleChainStage === 'ooo' ? 'lobby-return-green' : 'none';
        case 'manual-ooo':
            if (input.status !== 'out-of-office' || !input.oooSelectedManually) return 'none';
            return input.returnDateIsToday ? 'lobby-keep-status' : 'sign-out';
        case 'midnight':
            // keep only the user's own OOO details; an OOO the idle chain set becomes OOO today like any other status
            return input.status === 'out-of-office' && input.idleChainStage !== 'ooo' ? 'midnight-keep-ooo' : 'midnight-set-ooo-today';
    }
}

/** Lobby redirect reasons a prompt outcome can lead to (a subset of LobbyRedirectReason, redeclared so this file keeps relative imports only). */
export type OutcomeLobbyReason = 'inactivity-detected' | 'ooo-inactivity' | 'midnight-status-update';

export type OutcomeNavigation = { kind: 'none' } | { kind: 'lobby'; reason: OutcomeLobbyReason; preserveStatus: boolean } | { kind: 'sign-out' };

/** Whether this tab shows (and may time out) the prompt. The midnight prompt belongs to area tabs only, never the lobby. */
export function isPromptShownInTab(rule: PresenceRule, inArea: boolean): boolean {
    return rule !== 'midnight' || inArea;
}

/**
 * A tab that doesn't show the midnight prompt (the lobby) still records activity. Activity there after the
 * prompt opened means the user is present, so the pending midnight prompt is dismissed.
 */
export function shouldDismissPromptForActivity(
    prompt: { rule: PresenceRule; deadline: number },
    inArea: boolean,
    lastActivity: number,
    promptSeconds: number
): boolean {
    if (isPromptShownInTab(prompt.rule, inArea)) return false;
    const openedAt = prompt.deadline - promptSeconds * 1000;
    return lastActivity > openedAt;
}

/** Where a tab that did NOT claim a prompt timeout should go once another tab ran the timeout action. */
export function outcomeNavigation(action: TimeoutAction): OutcomeNavigation {
    switch (action) {
        case 'lobby-return-green':
            return { kind: 'lobby', reason: 'inactivity-detected', preserveStatus: false };
        case 'lobby-keep-status':
            return { kind: 'lobby', reason: 'ooo-inactivity', preserveStatus: true };
        case 'midnight-keep-ooo':
        case 'midnight-set-ooo-today':
            return { kind: 'lobby', reason: 'midnight-status-update', preserveStatus: true };
        case 'sign-out':
            return { kind: 'sign-out' };
        case 'none':
            return { kind: 'none' };
    }
}

/**
 * Production midnight detection. `lastSeenDate` is the Berlin date this tab last saw (kept across reloads),
 * so a midnight that passed while the laptop slept is still detected after the reconnect reload.
 */
export function detectBerlinMidnight(now: number, lastSeenDate: string | null): { nextSeenDate: string; midnightAt: number | null } {
    const today = berlinDateString(now);
    if (lastSeenDate === null || lastSeenDate === today) return { nextSeenDate: today, midnightAt: null };
    return { nextSeenDate: today, midnightAt: berlinMidnightTimestamp(now) };
}

/**
 * Last seen Berlin date a tab starts with. Only the automatic reload after a reconnect (e.g. waking from sleep)
 * keeps the stored date, so a midnight that passed during sleep is still detected. Any other page load starts
 * from today: the browser may restore sessionStorage with the session, and a next-morning start must not prompt.
 */
export function initialLastSeenBerlinDate(storedDate: string | null, today: string, isReconnectReload: boolean): string {
    return isReconnectReload && storedDate !== null ? storedDate : today;
}

export type LegacyManualFlag = 'away' | 'ooo';

/**
 * One-time migration of statuses the old server idle pipeline set: those left the manual flag at "false".
 * Adopting them into the client idle chain lets activity return the user to available.
 */
export function legacyStageMigration(
    status: UserStatus | undefined,
    awayFlag: string | null,
    oooFlag: string | null
): { stage: IdleChainStage; clearFlag: LegacyManualFlag } | null {
    if (status === 'away' && awayFlag === 'false') return { stage: 'away', clearFlag: 'away' };
    if (status === 'out-of-office' && oooFlag === 'false') return { stage: 'ooo', clearFlag: 'ooo' };
    return null;
}

export type StageSyncAction = 'none' | 'set-away' | 'set-ooo-today' | 'set-available';

/** How a tab follows another tab's idle-chain storage change (stage written, or chain returned to available). */
export function stageSyncAction(
    key: string | null,
    newValue: string | null,
    status: UserStatus | undefined,
    stage: IdleChainStage | null,
    oooSelectedManually: boolean,
    awaySelectedManually: boolean
): StageSyncAction {
    if (key === IDLE_CHAIN_STAGE_STORAGE_KEY) {
        if (newValue === 'away' && status === 'available') return 'set-away';
        if (newValue === 'ooo' && (status === 'available' || status === 'away')) return 'set-ooo-today';
        return 'none';
    }
    if (key === IDLE_CHAIN_RETURNED_STORAGE_KEY) {
        if ((status === 'away' || status === 'out-of-office') && stage === null && !oooSelectedManually && !awaySelectedManually) return 'set-available';
    }
    return 'none';
}
